import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { advertisementAPI, locationAPI } from '../../../services/api';
import { showSuccessToast, showErrorToast } from '../../../utils/notificationUtils';
import { resolveImageUrl } from '../../../utils/imageUtils';
import { 
  Plus, Search, Trash2, Edit, AlertCircle, RefreshCw, 
  ArrowLeft, GripVertical, Calendar, Clock, ExternalLink, Copy, X
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AdvertisementForm from './AdvertisementForm';
import './AdvertisementManager.css';

const AdvertisementManager = ({ initialView = 'summary' }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { countryCode, advertisement_id } = useParams();
  
  const [view, setView] = useState(initialView); // 'summary', 'list', 'form'
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [advertisements, setAdvertisements] = useState([]);
  const [selectedAd, setSelectedAd] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [allCountries, setAllCountries] = useState([]);
  
  // Clone Modal State
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [adToClone, setAdToClone] = useState(null);
  const [cloneCountryCode, setCloneCountryCode] = useState('');

  // Sync view and selected country with URL params
  useEffect(() => {
    if (initialView === 'form') {
      setView('form');
      // If we have an advertisement_id but no selectedAd, it's a deep link or refresh
      if (advertisement_id && (!selectedAd || selectedAd.advertisement_id !== advertisement_id)) {
        // Try to find in current list first
        const foundLocal = advertisements.find(a => a.advertisement_id === advertisement_id);
        if (foundLocal) {
          setSelectedAd(foundLocal);
        } else {
          // Fetch from API using advertisement_id
          const fetchSingleAd = async () => {
             setLoading(true);
             try {
               const response = await advertisementAPI.getAdvertisement(advertisement_id);
               setSelectedAd(response.data?.data);
             } catch (error) {
               showErrorToast(t('error.fetchFailed'));
               navigate(`/admin/dashboard/cms/advertisements/${countryCode || ''}`);
             } finally {
               setLoading(false);
             }
          };
          fetchSingleAd();
        }
      } else if (!advertisement_id) {
        // Truly an "Add" mode - clear any previously selected ad!
        if (selectedAd) setSelectedAd(null);
      }
    } else if (countryCode && !location.pathname.endsWith('/add')) {
      if (view !== 'list' || (selectedCountry && selectedCountry.code !== countryCode)) {
         fetchAdsByCountry(countryCode);
      }
    } else if (!countryCode && !location.pathname.endsWith('/add')) {
      setView('summary');
      setSelectedCountry(null);
    }
  }, [countryCode, advertisement_id, initialView, advertisements.length]);

  useEffect(() => {
    const loadSummary = async () => {
      await fetchSummary();
    };

    if (view === 'summary') {
      loadSummary();
    } else if (view === 'list' && countryCode && countries.length === 0) {
      // If direct linked to list, fetch countries to get the name
      loadSummary();
    }

    // Fetch all countries for clone modal/selectors
    const fetchAllCountries = async () => {
      try {
        const response = await locationAPI.getCountries();
        setAllCountries(response.data?.data || []);
      } catch (error) {
        console.error('Failed to fetch all countries');
      }
    };
    fetchAllCountries();
  }, [view, countryCode]);

  useEffect(() => {
    // If countries were just loaded and we have a countryCode, update the selectedCountry object
    if (countries.length > 0 && countryCode && (!selectedCountry || selectedCountry.name_en === undefined)) {
      const found = countries.find(c => c.code === countryCode);
      if (found) setSelectedCountry(found);
    }
  }, [countries, countryCode]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await advertisementAPI.getSummary();
      setCountries(response.data?.data || []);
    } catch (error) {
      showErrorToast(t('error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAdsByCountry = async (code) => {
    setLoading(true);
    try {
      const response = await advertisementAPI.getAdvertisements({ country_code: code });
      setAdvertisements(response.data?.data || []);
      
      // Attempt to find country details from existing countries list if we have it
      if (countries.length > 0) {
        const found = countries.find(c => c.code === code);
        if (found) setSelectedCountry(found);
      } else {
        // If countries not loaded yet, we might need to fetch them to get the name
        // But for now, we'll just set the code if we can't find it
        setSelectedCountry({ code });
        // Trigger a wrap-around fetch if needed or let the summary fetch handle it
      }
      
      setView('list');
    } catch (error) {
      showErrorToast(t('error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCountryClick = (country) => {
    navigate(`/admin/dashboard/cms/advertisements/${country.code}`);
  };

  const handleEdit = (ad) => {
    navigate(`/admin/dashboard/cms/advertisements/${selectedCountry?.code || ad.country_code}/edit/${ad.advertisement_id}`);
  };

  const handleClone = (ad) => {
    setAdToClone(ad);
    setCloneCountryCode('');
    setShowCloneModal(true);
  };

  const confirmClone = () => {
    if (!cloneCountryCode) {
      showErrorToast(t('adminDashboard.cms.select_target_country'));
      return;
    }
    
    // Prepare cloned data
    const clonedData = {
      ...adToClone,
      country_code: cloneCountryCode,
      isClone: true,
      source_ad_id: adToClone.advertisement_id
    };
    
    setSelectedAd(clonedData);
    setShowCloneModal(false);
    setView('form');
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = (adId) => {
    setDeleteTarget(adId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await advertisementAPI.deleteAdvertisement(deleteTarget);
      showSuccessToast(t('success.deleted'));
      fetchAdsByCountry(selectedCountry?.code);
    } catch (error) {
      showErrorToast(t('error.deleteFailed'));
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(advertisements);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setAdvertisements(items);

    try {
      const ids = items.map(item => item.id);
      await advertisementAPI.reorderAdvertisements(ids);
    } catch (error) {
      showErrorToast(t('error.reorderFailed'));
      // Revert if failed
      fetchAdsByCountry(selectedCountry.code);
    }
  };

  const handleFormSuccess = () => {
    setSearchTerm(''); // Clear search on success
    if (selectedCountry) {
      fetchAdsByCountry(selectedCountry.code);
    } else {
      fetchSummary();
      setView('summary');
    }
  };

  const handleRefresh = () => {
    if (view === 'summary') {
      fetchSummary();
    } else if (view === 'list' && selectedCountry) {
      fetchAdsByCountry(selectedCountry.code);
    }
  };

  const filteredCountries = countries.filter(c => 
    c.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name_ar?.includes(searchTerm) ||
    c.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAds = advertisements.filter(ad => 
    ad.title_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ad.title_ar?.includes(searchTerm) ||
    ad.advertisement_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBack = () => {
    if (view === 'list') {
      navigate('/admin/dashboard/cms/advertisements');
    } else {
      navigate('/admin/dashboard');
    }
  };

  if (view === 'form') {
    return (
      <AdvertisementForm 
        ad={selectedAd}
        defaultCountryCode={selectedCountry?.code}
        onSuccess={() => {
          handleFormSuccess(); // This already handles refreshing the correct view
          navigate(selectedCountry?.code ? `/admin/dashboard/cms/advertisements/${selectedCountry.code}` : `/admin/dashboard/cms/advertisements`);
        }}
        onCancel={() => {
          navigate(selectedCountry?.code ? `/admin/dashboard/cms/advertisements/${selectedCountry.code}` : `/admin/dashboard/cms/advertisements`);
        }}
      />
    );
  }

  return (
    <div className="ad-manager-container" dir={i18n.dir()}>
      <div className="ad-manager-header">
        <div className="ad-header-left">
          {view === 'list' && (
            <button className="ad-back-btn" onClick={handleBack}>
              <ArrowLeft size={20} />
            </button>
          )}
          <h2 className="ad-manager-title">
            {view === 'list' && selectedCountry
              ? `${t('adminDashboard.menu.cms_advertisements', 'Advertisements')} - ${i18n.language === 'ar' ? selectedCountry.name_ar : selectedCountry.name_en}` 
              : t('adminDashboard.menu.cms_advertisements', 'Advertisements')}
          </h2>
        </div>
        <button 
          className="ad-add-btn" 
          onClick={() => navigate(selectedCountry?.code ? `/admin/dashboard/cms/advertisements/${selectedCountry.code}/add` : '/admin/dashboard/cms/advertisements/add')}
        >
          <Plus size={18} /> {t('common.addNew')}
        </button>
      </div>

      <div className="ad-manager-filters">
        <div className="ad-search-box">
          <Search className="ad-search-icon" size={18} />
          <input
            type="text"
            placeholder={view === 'summary' ? t('adminDashboard.cms.search_countries', 'Search countries...') : t('adminDashboard.cms.search_ads', 'Search ads...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="ad-refresh-btn" onClick={handleRefresh} disabled={loading} title={t('common.refresh')}>
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {view === 'summary' ? (
        loading ? (
          <div className="ad-country-grid">
            {Array(8).fill(0).map((_, i) => (
              <div key={i} className="ad-country-card loading-skeleton" style={{ height: '100px' }}></div>
            ))}
          </div>
        ) : filteredCountries.length > 0 ? (
          <div className="ad-country-grid">
            {filteredCountries.map(country => (
              <div key={country.code} className="ad-country-card" onClick={() => handleCountryClick(country)}>
                <div className="ad-country-icon">{country.code}</div>
                <div className="ad-country-info">
                  <div className="ad-country-name">{i18n.language === 'ar' ? country.name_ar : country.name_en}</div>
                  <div className="ad-country-count">
                    <span>{t('adminDashboard.cms.ad_count', 'Ads')}:</span>
                    <span className="count-badge">{country.ad_count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ad-empty-container">
            <AlertCircle size={48} className="ad-empty-icon" />
            <h3>{searchTerm ? t('common.noResultsFound', 'No results found') : t('common.noData', 'No data available')}</h3>
            {searchTerm && <p>{t('cms.search_no_match', 'We couldn\'t find anything matching your search.')}</p>}
          </div>
        )
      ) : (
        <div className="ad-list-view">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="advertisements">
              {(provided) => (
                <div className="ad-items-list" {...provided.droppableProps} ref={provided.innerRef}>
                  {filteredAds.length > 0 ? (
                    filteredAds.map((ad, index) => (
                      <Draggable key={ad.id} draggableId={ad.id.toString()} index={index}>
                        {(provided) => (
                          <div 
                            className="ad-item-row"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <div className="ad-drag-handle" {...provided.dragHandleProps}>
                              <GripVertical size={20} />
                            </div>
                            
                            <div className="ad-preview-container">
                              {ad.media && ad.media[0] ? (
                                ad.media[0].file_type === 'video' ? (
                                  <video src={resolveImageUrl(ad.media[0].url)} muted />
                                ) : (
                                  <img src={resolveImageUrl(ad.media[0].url)} alt={ad.title_en} />
                                )
                              ) : (
                                <div className="no-image-placeholder">No Media</div>
                              )}
                            </div>

                            <div className="ad-content-info">
                              <div className="ad-item-id">{ad.advertisement_id}</div>
                              <div className="ad-item-title">{i18n.language === 'ar' ? ad.title_ar : ad.title_en}</div>
                              <div className="ad-item-meta">
                                <span className={`ad-status-indicator ${ad.is_active ? 'status-active' : 'status-inactive'}`}>
                                  {ad.is_active ? t('common.active') : t('common.inactive')}
                                </span>
                                {ad.expiry_date && (
                                  <div className="meta-item">
                                    <Calendar size={14} />
                                    <span>{t('adminDashboard.cms.expires', 'Expires')}: {new Date(ad.expiry_date).toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="ad-actions">
                              <button 
                                className="action-btn action-edit" 
                                onClick={() => handleEdit(ad)}
                                title={t('common.edit')}
                              >
                                <Edit size={16} />
                              </button>
                              <button 
                                className="action-btn action-clone" 
                                onClick={() => handleClone(ad)}
                                title={t('adminDashboard.cms.clone')}
                              >
                                <Copy size={16} />
                              </button>
                              <button 
                                className="action-btn action-delete" 
                                onClick={() => handleDelete(ad.advertisement_id)}
                                title={t('common.delete')}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <div className="ad-empty-container">
                      <AlertCircle size={48} className="ad-empty-icon" />
                      <h3>{searchTerm ? t('common.noResultsFound', 'No results found') : t('cms.no_ads_found', 'No advertisements found')}</h3>
                      {searchTerm && <p>{t('cms.search_no_match', 'We couldn\'t find anything matching your search.')}</p>}
                    </div>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}

      {/* ── Clone Modal ── */}
      {showCloneModal && (
        <div className="clone-modal-overlay">
          <div className="clone-modal-content">
            <div className="clone-modal-header">
              <h3>{t('adminDashboard.cms.clone_ad')}</h3>
              <button 
                className="close-modal-btn" 
                onClick={() => setShowCloneModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="clone-modal-body">
              <p>{t('adminDashboard.cms.select_target_country')}</p>
              <select 
                value={cloneCountryCode}
                onChange={(e) => setCloneCountryCode(e.target.value)}
                className="clone-country-select"
              >
                <option value="">{t('adminDashboard.cms.select_country')}</option>
                {allCountries.map(c => (
                  <option key={c.code} value={c.code}>
                    {i18n.language === 'ar' ? c.name_ar : c.name_en} ({c.code})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="clone-modal-footer">
              <button 
                className="cancel-btn" 
                onClick={() => setShowCloneModal(false)}
              >
                {t('cancel')}
              </button>
              <button 
                className="confirm-btn" 
                onClick={confirmClone}
                disabled={!cloneCountryCode}
              >
                <Copy size={18} />
                {t('adminDashboard.cms.confirm_clone')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{t('adminDashboard.cms.confirmDelete')}</h3>
            <p>{t('adminDashboard.cms.deleteConfirmationMessage')}</p>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={confirmDelete}>{t('common.confirm')}</button>
              <button className="btn btn-secondary" onClick={cancelDelete}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertisementManager;
